/*eslint max-lines-per-function: ["error", 190], complexity: ["error", 24]*/

import React, { useEffect, useState } from 'react';
import { Alert, Form, Modal } from 'react-bootstrap';

import ValidationAlert from '../../../app/components/ValidationAlert';
import {
  CreateUpdateOrderDto,
  CreateUpdateOrderDtoStatusEnum,
  GetOrderDtoStatusEnum,
  GetOrderEstimateItemDto,
  GetProjectDto,
} from '../../../common/pokCore/autogenerated/pokApiClient/models';
import { useNotifications } from '../../../common/hooks/useNotifications';
import FormRow from '../../../app/components/FormRow';
import DynamicTextarea from '../../../app/components/DynamicTextarea';
import { OrderStatusObj } from '../../../common/pokCore/validation/enums';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { usePokCore } from '../../../common/hooks/usePokCore';
import Button from '../../../common/components/Button/Button';
import { PermissionsEnum } from '../../../common/pokCore/authorization/permissions';
import { useAuth } from '../../../common/hooks/useAuth';
import { useTranslation } from '../../../common/hooks/useTranslation';

import { OrderForm } from './OrderForm';

interface OrderEditorProps {
  id?: string;
  systemNumber?: number;
  project: GetProjectDto;
  resultProject?: GetProjectDto;
  order: CreateUpdateOrderDto;
  deactivateOrder: (id: string) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
  handleClose?: () => void;
  readOnly?: boolean;
  show?: boolean;
  status?: GetOrderDtoStatusEnum;
  orderEstimateItems?: GetOrderEstimateItemDto[];
  rejectedNote?: string;
  currencyCode?: string;
  onCompleted: (
    order: CreateUpdateOrderDto,
    id?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const OrderEditor: React.FC<OrderEditorProps> = props => {
  const [order, setOrder] = useState(props.order);
  const [emails, setEmails] = useState(props.order.emails || []);
  const [orderEstimateItemsIds, setOrderEstimateItemsIds] = useState<string[]>(
    props.order.orderEstimateItemsIds || [],
  );
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isLoadingRemove, setIsLoadingRemove] = useState(false);
  const [estimateItemsFilterChanged, setEstimateItemsFilterChanged] =
    useState(false);

  const notifications = useNotifications();
  const pok = usePokCore();
  const auth = useAuth();
  const { t, tk } = useTranslation('orderLng');

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setOrder(props.order);
      setEstimateItemsFilterChanged(false);
    }
  }, [props.order, props.show]);

  const createOrder = async () => {
    setIsLoadingSave(true);
    props
      .onCompleted({ ...order, emails, orderEstimateItemsIds }, order.id)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          return;
        }
        notifications.saveCompleted();
      })
      .catch(async errorResponse => {
        notifications.caughtError(errorResponse);
      })
      .finally(() => setIsLoadingSave(false));
  };

  const deactivateOrder = async () => {
    setIsLoadingRemove(true);
    props
      .deactivateOrder(order.id)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          return;
        }
        notifications.saveCompleted();
      })
      .catch(async errorResponse => {
        notifications.caughtError(errorResponse);
      })
      .finally(() => setIsLoadingRemove(false));
  };

  const propertyChange = (obj: Partial<CreateUpdateOrderDto>) => {
    setOrder({ ...order, ...obj });
  };

  useEffect(() => {
    setEmails(props.order.emails);
    setOrderEstimateItemsIds(props.order.orderEstimateItemsIds);
  }, [props.order.emails, props.order.orderEstimateItemsIds]);

  const authorized = order?.id
    ? auth.check(PermissionsEnum.Administration, pok.getCompanyId())
    : false;

  return (
    <>
      <Modal
        onHide={props.handleClose}
        show={props.show}
        centered={true}
        keyboard={true}
        backdrop="static"
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {props.id
              ? props.readOnly
                ? t(tk.order.orderDetails, { sysNumber: props?.systemNumber })
                : t(tk.order.orderEdit, { sysNumber: props?.systemNumber })
              : t(tk.order.createOrder)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <OrderForm
            order={order}
            project={props.project}
            resultProject={props?.resultProject}
            propertyChange={propertyChange}
            setEmails={mail => {
              setEmails(mail);
            }}
            emails={emails}
            setOrderEstimateItems={ids => {
              setOrderEstimateItemsIds(ids);
            }}
            readOnly={props.readOnly}
            orderEstimateItems={props.orderEstimateItems}
            currencyCode={props.currencyCode}
            onEstimateItemsFilterChanged={setEstimateItemsFilterChanged}
          />
          <Form className="d-grid gap-4 mb-3">
            {props.status && (
              <FormRow controlId="status" label={t(tk.common.status)}>
                <Form.Control
                  value={
                    t(OrderStatusObj[props.status]) +
                    (!props.order.active ? ` - ${t(tk.order.deleted)}` : '')
                  }
                  disabled
                />
              </FormRow>
            )}
            {props.rejectedNote && (
              <FormRow
                controlId="rejectedNote"
                label={t(tk.order.rejectedNote)}
              >
                <DynamicTextarea disabled readOnly value={props.rejectedNote} />
              </FormRow>
            )}
          </Form>
          {estimateItemsFilterChanged &&
            order.id &&
            order.status === CreateUpdateOrderDtoStatusEnum.New &&
            order.active && (
              <Alert variant="info">
                {t(tk.order.estimateItemsListUpdated)}
              </Alert>
            )}
          <ValidationAlert show={showSaveErrors} errors={saveErrors} />
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between align-items-center w-100">
            {props.id && (
              <LastEditorInfo
                id={props.id}
                className="color-primary"
                method={pok.orders.getLastEditor}
              />
            )}
            <div className="d-flex marginLeft gap-2">
              {((order.id && !props.readOnly) || authorized) &&
                props.order.active && (
                  <ConfirmationButton
                    onOK={deactivateOrder}
                    confirmation={t(tk.order.askDeleteOrder)}
                    variant="outline-danger"
                    isLoading={isLoadingRemove}
                  >
                    {t(tk.button.delete)}
                  </ConfirmationButton>
                )}
              <Button variant="outline-secondary" onClick={props.handleClose}>
                {t(tk.button.close)}
              </Button>
              {!props.readOnly && (
                <Button
                  variant="primary"
                  onClick={createOrder}
                  isLoading={isLoadingSave}
                >
                  {t(tk.button.save)}
                </Button>
              )}
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};
